import GridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import TileFrame from './TileFrame.jsx';
import Carousel from './Carousel.jsx';
import SectionGrid from './SectionGrid.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
// Grid geometry (order, widths, mobile heights) lives in one place so the public
// share page lays a shared report out exactly like this dashboard.
import { COLS, ROW_HEIGHT, GRID_MARGIN, isMetricTile, isKpiCard, mobileTileHeight, orderedItems, stackGroups, keepsRow, gridLayout, moveInReadOrder } from '../lib/tileLayout.js';

const Grid = WidthProvider(GridLayout);

// Renders tiles AND carousels on a 24-column grid. Carousels are full-width
// (w=24) grid items, so they can be dragged anywhere (incl. between grid rows)
// and resized like any tile. Mirrors Looker's grid units.
//
// On phones (<768px) we drop the pixel grid entirely and stack everything in a
// single full-width, read-only column ordered by grid position — tiny squished
// tiles don't work on a 375px screen. The EDITOR on a phone gets the same
// read-order stack with ↑/↓ controls instead (ArrangeStack).
export default function EditableGrid(props) {
  const isMobile = useIsMobile();
  if (isMobile && props.editable && props.onLayoutChange) return <ArrangeStack {...props} />;
  return isMobile ? <StackedGrid {...props} /> : <DesktopGrid {...props} />;
}

// The EDITOR on a phone. The viewer's stack is read-only, and the editor used to
// render that same stack — so on a phone a dashboard's order could not be
// changed at all (ticket #27: "once imported I cannot modify it"). Here every
// item sits on its own line in read order with ↑/↓ (and ✎ for tiles); each tap
// is written back as desktop grid coordinates (moveInReadOrder), so the desktop
// grid, the phone stack and shared reports all follow. Hidden tiles are listed
// too (dimmed) — the editor is where they get un-hidden.
function ArrangeStack({ tiles = [], carousels = [], filterValues, onLayoutChange, onEditTile, onHideTile }) {
  const items = orderedItems(tiles, carousels);
  const move = (id, dir) => {
    const map = moveInReadOrder(tiles, carousels, id, dir);
    if (map) onLayoutChange(map);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it, idx) => {
        const el = it.el;
        const isTile = it.kind === 'tile';
        const label = (isTile ? el.title : el.title || (el.mode === 'grid' ? 'Section' : 'Carousel')) || '(untitled)';
        return (
          <div key={el.id} style={{ opacity: isTile && el.hidden ? 0.5 : 1 }}>
            <div style={arrangeBar}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600 }}>
                {isTile ? '' : '▤ '}{label}{isTile && el.hidden ? ' (hidden)' : ''}
              </span>
              <button type="button" style={arrangeBtn} disabled={idx === 0} onClick={() => move(el.id, -1)} aria-label={`Move ${label} up`}>↑</button>
              <button type="button" style={arrangeBtn} disabled={idx === items.length - 1} onClick={() => move(el.id, 1)} aria-label={`Move ${label} down`}>↓</button>
              {isTile && onHideTile && <button type="button" style={arrangeBtn} onClick={() => onHideTile(el.id)} aria-label={el.hidden ? `Show ${label}` : `Hide ${label}`}>{el.hidden ? 'Show' : 'Hide'}</button>}
              {isTile && onEditTile && <button type="button" style={arrangeBtn} onClick={() => onEditTile(el.id)} aria-label={`Edit ${label}`}>✎</button>}
            </div>
            {isTile ? (
              <div style={{ height: mobileTileHeight(el) }}>
                <TileFrame tile={el} filterValues={filterValues} editable={false} />
              </div>
            ) : (
              <div style={{ height: 180, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 10 }}>
                {el.mode === 'grid'
                  ? <SectionGrid carousel={el} filterValues={filterValues} editable={false} />
                  : <Carousel carousel={el} filterValues={filterValues} editable={false} />}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const arrangeBar = { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 };
const arrangeBtn = { minWidth: 40, height: 40, padding: '0 10px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 16, fontWeight: 700, cursor: 'pointer' };

function StackedGrid({ tiles = [], carousels = [], filterValues }) {
  // Order by grid position (row, then column) so the stack reads the same way
  // the dashboard looks on desktop.
  const items = orderedItems(
    tiles.filter((t) => !t.hidden),
    carousels.filter((c) => (c.tiles || []).some((t) => !t.hidden)),
  );
  const groups = stackGroups(items);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {groups.map((g, gi) => {
        if (g.type === 'carousel') {
          const c = g.it.el;
          // A grid "section": stack its tiles 2-up like the main mobile view.
          if (c.mode === 'grid') {
            const stiles = (c.tiles || []).filter((t) => !t.hidden).slice().sort((a, b) => (a.layout?.y ?? 0) - (b.layout?.y ?? 0) || (a.layout?.x ?? 0) - (b.layout?.x ?? 0));
            return (
              <div key={c.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                {c.title && <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, textAlign: c.titleAlign || 'left' }}>{c.title}</h3>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, gridAutoFlow: 'dense' }}>
                  {stiles.map((t) => (
                    <div key={t.id} style={{ height: mobileTileHeight(t), gridColumn: isMetricTile(t) ? 'auto' : '1 / -1' }}>
                      <TileFrame tile={t} filterValues={filterValues} editable={false} />
                    </div>
                  ))}
                  {stiles.length === 0 && <div style={{ gridColumn: '1 / -1', color: '#bbb', fontSize: 13 }}>No tiles</div>}
                </div>
              </div>
            );
          }
          // A scrolling carousel: a compact capped swipe band — unless it holds
          // charts/tables (which get full-width cards on mobile and need real
          // height to be readable, like a standalone mobile chart).
          const hasBigCards = (c.tiles || []).some((t) => !t.hidden && !isKpiCard(t));
          return (
            <div key={c.id} style={{ height: hasBigCards ? 320 : Math.min(230, Math.max(150, g.it.h * 16)), background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <Carousel carousel={c} filterValues={filterValues} editable={false} />
            </div>
          );
        }
        // A desktop row of tiles. Keep a strip of small tiles (each ≤ half-width)
        // on one line, sharing the width proportionally; otherwise stack them.
        const keepRow = keepsRow(g);
        if (keepRow) {
          const rowH = Math.max(0, ...g.items.map((it) => mobileTileHeight(it.el) || 0));
          return (
            <div key={`row-${gi}`} style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
              {g.items.map((it) => (
                <div key={it.el.id} style={{ flex: `${it.w} 1 0`, minWidth: 80, height: rowH || undefined }}>
                  <TileFrame tile={it.el} filterValues={filterValues} editable={false} />
                </div>
              ))}
            </div>
          );
        }
        return (
          <div key={`stack-${gi}`} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {g.items.map((it) => (
              <div key={it.el.id} style={{ height: mobileTileHeight(it.el) }}>
                <TileFrame tile={it.el} filterValues={filterValues} editable={false} />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function DesktopGrid({ tiles = [], carousels = [], filterValues, editable, onLayoutChange, onEditTile, onApplyTileQuery, onDuplicateTile, onRemoveTile, onHideTile, carouselHandlers }) {
  // Viewers don't see hidden tiles (or carousels left with nothing visible).
  const visTiles = editable ? tiles : tiles.filter((t) => !t.hidden);
  const visCarousels = editable ? carousels : carousels.filter((c) => (c.tiles || []).some((t) => !t.hidden));
  const layout = gridLayout(visTiles, visCarousels);

  function handleChange(newLayout) {
    if (!onLayoutChange) return;
    const map = {};
    for (const item of newLayout) map[item.i] = { x: item.x, y: item.y, w: item.w, h: item.h };
    onLayoutChange(map);
  }

  return (
    <Grid
      className="layout"
      layout={layout}
      cols={COLS}
      rowHeight={ROW_HEIGHT}
      margin={GRID_MARGIN}
      isDraggable={!!editable}
      isResizable={!!editable}
      draggableHandle=".tile-drag-handle"
      onDragStop={handleChange}
      onResizeStop={handleChange}
      compactType="vertical"
      preventCollision={false}
    >
      {visTiles.map((tile) => (
        <div key={tile.id}>
          <TileFrame
            tile={tile}
            filterValues={filterValues}
            editable={editable}
            onEdit={() => onEditTile?.(tile.id)}
            onApplyQuery={onApplyTileQuery ? (query, opts) => onApplyTileQuery(tile.id, query, opts) : undefined}
            onDuplicate={() => onDuplicateTile?.(tile.id)}
            onRemove={() => onRemoveTile?.(tile.id)}
            onToggleHide={onHideTile ? () => onHideTile(tile.id) : undefined}
          />
        </div>
      ))}
      {visCarousels.map((c) => (
        <div key={c.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          {c.mode === 'grid'
            ? <SectionGrid carousel={c} filterValues={filterValues} editable={editable} {...(carouselHandlers ? carouselHandlers(c) : {})} />
            : <Carousel carousel={c} filterValues={filterValues} editable={editable} {...(carouselHandlers ? carouselHandlers(c) : {})} />}
        </div>
      ))}
    </Grid>
  );
}
